import { UserIcon } from '@phosphor-icons/react';
import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import Logo from '../../Assets/Img/Black-Logo.svg';
import CONSTANTS from '../../Constants';
import { useCustomNavigation, useStores } from '../../Hooks';
import {
    isNavCategoryWithGarments,
    NAV_CATEGORY_IDS,
    NAV_CATEGORY_TITLES,
    NavCategoryId,
    useGetCategories,
} from '../../Hooks/useGetCategories';
import { useGetSkins } from '../../Hooks/useGetSkins';
import Copydeck from '../../i18n/Copydeck';
import { garmentService } from '../../Services';
import { getUrlForEnvironment } from '../../Utils/environment';
import { POCToggle } from '../POCToggle';
import Button from '../ui/Button/Button';
import Link from '../ui/Link/Link';
import { Skeleton } from '../ui/Skeleton';
import './index.scss';
import { SideNav } from './SideNav';

const Navbar = () => {
    const customNavigate = useCustomNavigation();
    const { authStore, userStore } = useStores();

    const getManageAccountURL = () => getUrlForEnvironment(CONSTANTS.MANAGE_ACCOUNT_URL);

    const handleLogout = () => {
        try {
            authStore.clearAuthInfo();
            localStorage.clear();
            userStore.clearUser();
            customNavigate(CONSTANTS.SCREEN.LOGIN);
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <nav className="Navbar">
            <div className="Navbar__content">
                <div className="sideNav">
                    <SideNav handleLogout={handleLogout} />
                </div>
                <div className="Navbar__buttonGroups">
                    <CategoryNavItems />
                    <OutfitsNavItem />
                </div>
                <div className="Navbar__title">
                    <BrandLogo />
                </div>
                <div className="Navbar__buttonGroups -right">
                    <Popover.Root>
                        <Popover.Trigger asChild>
                            <Button variant="text" aria-label="User menu">
                                <UserIcon size={32} weight="thin" />
                            </Button>
                        </Popover.Trigger>
                        <Popover.Portal>
                            <Popover.Content className="popoverContent" sideOffset={5} align="end">
                                <div className="Navbar__userMenu">
                                    <Button variant="text" asChild>
                                        <a
                                            href={getManageAccountURL()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: 'none', color: 'inherit' }}
                                        >
                                            Manage Account
                                        </a>
                                    </Button>
                                    <Button
                                        variant="text"
                                        onClick={handleLogout}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                    >
                                        {Copydeck.navbarNavbarLogout}
                                    </Button>
                                    {CONSTANTS.IS_DEV || CONSTANTS.IS_STAGE ? (
                                        <div className="pocToggle">
                                            <label htmlFor="poc-toggle">Enable POC</label>
                                            <POCToggle />
                                        </div>
                                    ) : null}
                                </div>
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>
                </div>
            </div>
            {/* <CategoryDrawer open={isMenuOpen} /> */}
        </nav>
    );
};

export default Navbar;

const BrandLogo = () => {
    const { isPending, data } = useGetSkins();

    if (isPending) {
        return <Skeleton width={120} height={40} />;
    }

    return (
        <Link to="/">
            <img
                src={data?.value.logo_dark ? data?.value.logo_dark : Logo}
                alt="SpreeAI logo"
                className="logo"
            />
        </Link>
    );
};

const CategoryNavItems = () => {
    const { isLoading, isError, data } = useGetCategories();

    if (isLoading) {
        return (
            <>
                {NAV_CATEGORY_IDS.map((id) => (
                    <Skeleton key={id} width={42} height={24} />
                ))}
            </>
        );
    }

    const visibleItems: { id: NavCategoryId; name: string }[] = isError
        ? NAV_CATEGORY_IDS.map((id) => ({ id, name: NAV_CATEGORY_TITLES[id] }))
        : (data?.categories.filter(isNavCategoryWithGarments) ?? []);

    return (
        <>
            {visibleItems.map((category) => (
                <Button key={category.id} variant="text" asChild>
                    <Link
                        to={`/collections/${category.id}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                        {category.name}
                    </Link>
                </Button>
            ))}
        </>
    );
};

const OutfitsNavItem = () => {
    const { isPending, data } = useQuery({
        queryKey: ['outfits', 'all'],
        queryFn: () => garmentService.fetchAllOutfits(),
    });
    if (isPending) {
        return <Skeleton width={42} height={24} />;
    }

    const hasAvailableOutfit = data?.outfits.some((outfit) => outfit.status === 'AVAILABLE');
    if (!hasAvailableOutfit) {
        return null;
    }

    return (
        <Button variant="text" asChild>
            <Link to="/outfits" style={{ textDecoration: 'none', color: 'inherit' }}>
                Outfits
            </Link>
        </Button>
    );
};
